import { z } from 'zod';

/**
 * Primitivos de fronteira comuns a **todas** as respostas do `ApiCentriumOAuth`
 * (AD-165).
 *
 * Existem porque o JSON que o ERP real devolve não é o que o
 * `ApiCentriumOAuth.yaml` desenha, em dois pontos que se repetem endpoint a
 * endpoint. Ambos foram confirmados ao vivo em 2026-09-04 contra o ERP de
 * demonstração (tenant `c0lj6mvzeh`, empresa 1), com `curl`, sem passar pelo
 * BFF — não são suposição sobre o contrato, são o contrato observado:
 *
 * 1. **Número decimal e `int64` chegam como string JSON.** `"PrecoVenda1":
 *    "1.0000"`, `"ValorTotal": "89.50"`, `"ClienteDefaultCodigo": "999999"`,
 *    `"CondicaoPrazo": "0.00000"`. Só `int32` (`CodigoGrupo`, `TotalRegistros`,
 *    `PaginaAtual`) vem como número nativo. Um `z.number()` puro reprova a
 *    resposta inteira.
 * 2. **O envelope de saída some quando a procedure tem um único parâmetro de
 *    saída.** `GetProduto` devolve o SDT do produto na raiz, não
 *    `{"Produto": …}`; o mesmo vale para `GetCliente`, `GetSessao`,
 *    `GetListaProdutos`, `GetListaClientes`, `ListaDAVs`, `GetListaNFCes`,
 *    `GetListaVendedores` e `CarregarNFCe`.
 *
 *    **O envelope não pertence a endpoint nenhum — pertence à presença de
 *    `messages`** (AD-218, 2026-09-11). Com a coleção vazia sobra um único
 *    parâmetro de saída e o SDT vai para a raiz; com mensagem a devolver, os
 *    dois aparecem e o SDT volta para dentro da chave nomeada. O **mesmo**
 *    endpoint responde das duas formas: `GetDav` e `CarregarNFCe` envelopam a
 *    recusa e entregam o sucesso flat. A lista de "endpoints que mantêm o
 *    envelope" que este comentário trazia até 2026-09-11 (`GetDav` e
 *    `FaturarNFCe`) nasceu de uma amostra que só continha recusas — foi ela que
 *    fez `faturarNFCe.schema.ts` errar (AD-208) e, depois, `getDavOutputSchema`
 *    reprovar toda importação de DAV bem-sucedida (AD-218). Por isso a regra
 *    prática é aplicar `semEnvelope` **sempre**, e nunca decidir por endpoint.
 *
 * Os dois helpers são deliberadamente **tolerantes**, não substitutivos: aceitam
 * tanto o formato real quanto o do YAML. Não é indulgência — é a única forma
 * segura enquanto as duas formas coexistem no mundo (o ERP de demonstração de
 * hoje e a versão de KB que o `ApiCentriumOAuth.yaml` documenta), e é o que
 * permite ao `erp-mock` dos testes E2E continuar espelhando o YAML sem que a
 * suíte deixe de exercitar o caminho de produção.
 */

/**
 * Texto que representa um número decimal, do jeito que o GeneXus serializa
 * (`"1.0000"`, `"-203.000"`, `"0.00000"`, `"999999"`).
 *
 * Restrito de propósito: `Number('')` é `0` e `Number(' ')` também, então uma
 * string vazia num campo de preço viraria "R$ 0,00" em silêncio. Aqui ela é
 * erro de fronteira, como qualquer outro corpo fora do contrato.
 */
const TEXTO_NUMERICO = /^\s*[+-]?\d+(?:\.\d+)?\s*$/;

const textoNumericoSchema = z.string().regex(TEXTO_NUMERICO).transform(Number);

/**
 * Campo numérico do ERP: aceita `number` (int32, e o que o `erp-mock` produz) e
 * a string numérica que o ERP real devolve para `double`/`int64`.
 *
 * Substitui `z.number()` em todo schema de resposta. Não é `z.coerce.number()`:
 * aquele converte `true` em `1`, `null` em `0` e `""` em `0` — três corpos
 * inválidos que passariam a atravessar a fronteira como valor plausível.
 */
export const numeroErp = z.union([z.number(), textoNumericoSchema]);

/** Mesmo contrato de `numeroErp`, exigindo inteiro depois da conversão. */
export const inteiroErp = numeroErp.pipe(z.number().int());

/**
 * Aceita a resposta com **ou** sem o envelope nomeado do YAML, e devolve sempre
 * o conteúdo interno.
 *
 * O desembrulho acontece no `preprocess`, antes da validação: quando a chave
 * está presente o schema interno valida o valor dela; quando não está — o caso
 * do ERP real — valida a própria raiz. Como o resultado é o objeto interno nos
 * dois caminhos, quem consome não precisa saber qual formato chegou.
 *
 * `messages`, quando existe ao lado do envelope, é descartado aqui — este
 * helper entrega só o SDT. Quem precisa do motivo de uma recusa lê a raiz com
 * `recusaDeNegocio` **antes** de validar.
 */
export function semEnvelope<S extends z.ZodType>(
  chave: string,
  interno: S,
): z.ZodType<z.output<S>> {
  return z.preprocess((valor: unknown) => {
    if (typeof valor === 'object' && valor !== null && !Array.isArray(valor) && chave in valor) {
      return (valor as Record<string, unknown>)[chave];
    }
    return valor;
  }, interno);
}

/** `Type` de `GeneXus.Common.Messages_Message` que significa erro, não aviso. */
const TIPO_MENSAGEM_ERRO = 1;

const mensagemDoErpSchema = z.looseObject({
  Type: z.number().int().optional(),
  Description: z.string().optional(),
});

const respostaComMensagensSchema = z.looseObject({
  messages: z.array(mensagemDoErpSchema).optional(),
});

/**
 * O motivo da recusa que o ERP escreveu em `messages[]`, ou `null` quando a
 * resposta não é uma recusa.
 *
 * **Existe porque uma recusa de negócio não é uma resposta malformada, e vinha
 * sendo tratada como se fosse.** No padrão GeneXus o ERP recusa com `200` e o
 * SDT zerado, pondo a razão em `messages[].Description` — foi o que ele
 * respondeu a `CarregarNFCe` ("Série é obrigatório") e a `GetDav` ("Erro - Item
 * Liberado: S, Pedido Liberado: S, Status Digitação: N") nos casos relatados em
 * 2026-09-11. O schema reprova o SDT zerado, e com razão: importá-lo iniciaria
 * uma venda vazia com `clienteCodigo: 0`. Só que a falha de fronteira chegava ao
 * operador como "o ERP devolveu este documento em formato inesperado", jogando
 * fora a única frase que dizia o que fazer a respeito.
 *
 * Ler a raiz, e não o conteúdo do envelope: `messages` é irmão do SDT, fora do
 * que `semEnvelope` devolve.
 *
 * Só `Type: 1` conta. Aviso (`Type: 0`/`2`) acompanha resposta bem-sucedida — o
 * próprio `PostCliente` confirma o cadastro com `Type: 2` —, e tratá-lo como
 * recusa transformaria sucesso em erro.
 */
export function recusaDeNegocio(corpo: unknown): string | null {
  return mensagensDeErro(corpo)[0] ?? null;
}

/**
 * **Todas** as descrições de erro (`Type: 1`) de `messages[]`, na ordem do ERP,
 * sem as vazias.
 *
 * `recusaDeNegocio` fica com a primeira porque é a frase da recusa; esta existe
 * para quem precisa do detalhe que o ERP manda em seguida — o `GetProduto` de
 * cenário inválido devolve "Cenário não encontrado!" **e** a linha "Cenário
 * pesquisado: …" com a UF e a classificação fiscal que ele procurou (medido em
 * 2026-10-01, AD-258), e é a segunda que diz ao operador o que corrigir no ERP.
 */
export function mensagensDeErro(corpo: unknown): readonly string[] {
  const lido = respostaComMensagensSchema.safeParse(corpo);
  if (!lido.success) {
    return [];
  }

  const descricoes: string[] = [];
  for (const mensagem of lido.data.messages ?? []) {
    const descricao = mensagem.Description?.trim() ?? '';
    if (mensagem.Type === TIPO_MENSAGEM_ERRO && descricao !== '') {
      descricoes.push(descricao);
    }
  }
  return descricoes;
}
